import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { get, isEmpty } from "lodash";
import { Helmet } from "react-helmet-async";
import { EMPTY_STRING, REGEX, DEPLOY_STATUSES } from "../../constants";
import {
  whiteCopyIcon,
  whiteTickIcon,
  redirectIcon,
  errorIcon,
  dbSidePanelIcon,
  spinnerIcon,
} from "../../assets/icons";
import { EmptyLayout } from "../../components/molecules";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { actions } from "./applications.reducers";
import { selectAppDeployStatus, selectSelectedApp } from "./index";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { getFormattedDate } from "../../helpers";
import { selectSelectedDataModel } from "../DataModels";
import { setShouldShowDeploymentModal } from "../../components/organisms/Modal";

const { setSelectedAppId, setRecentlySelectedApp } = actions;
const { setSelectedTab } = breadCrumbActions;

const { FETCH_DATA_MODELS, FETCH_APPLICATION_BY_ID } = SPINNER_NAMES;
const { UPDATE_CASE } = REGEX;
const { SUCCESS, FAILURE, REPO_NOT_FOUND, IN_PROGRESS } = DEPLOY_STATUSES;

const BACKEND_URL = "BACKEND_URL";
const FRONTEND_URL = "FRONTEND_URL";
const RequiredCards = [
  "collections",
  "services",
  "backgroundTasks",
  "endpoints",
];
const deploymentFailureStatus = [REPO_NOT_FOUND, FAILURE];

const LoaderComponent = () => (
  <img
    src={spinnerIcon}
    className="inline animate-spin h-8 w-8"
    alt="spinner-icon"
  />
);

const RenderDeploymentStatus = ({
  shouldShowDeployUrl,
  frontendUrl,
  backendUrl,
  appId,
  updateDate,
}) => {
  const navigate = useNavigate();
  const [shouldShowTickOnCopy, setShouldShowTickOnCopy] = useState();

  const handleCopyToClipboard = (key, name) => {
    navigator.clipboard.writeText(key).then(() => {
      setShouldShowTickOnCopy(name);
      setTimeout(() => setShouldShowTickOnCopy(false), 1000);
    });
  };
  return (
    <>
      {shouldShowDeployUrl === SUCCESS ? (
        <div className=" bg-gray-800 gap-2 p-6 rounded-lg shadow-md w-full mb-4 border-2 border-white">
          <div className="flex justify-between">
            <div className="w-48 text-md py-2 text-white">Frontend URL</div>
            <div className="w-full flex justify-between bg-gray-900 px-4 py-2 border mb-4 rounded">
              <Link
                to={frontendUrl}
                target="_blank"
                className="text-indigo-600 hover:text-indigo-200"
                onClick={(e) => e.stopPropagation()}
              >
                {frontendUrl}
              </Link>
              <div className="flex">
                <button
                  className="w-10 rounded-full grid place-items-center hover:bg-gray-700/50"
                  onClick={() =>
                    handleCopyToClipboard(frontendUrl, FRONTEND_URL)
                  }
                >
                  <img
                    src={
                      shouldShowTickOnCopy === FRONTEND_URL
                        ? whiteTickIcon
                        : whiteCopyIcon
                    }
                    className="w-4"
                    alt={shouldShowTickOnCopy ? "tick-icon" : "copy-icon"}
                  />
                </button>
                <button
                  className="w-10 rounded-full grid place-items-center hover:bg-gray-700/50"
                  onClick={() => window.open(frontendUrl, "_blank")}
                >
                  <img src={redirectIcon} className="w-4" alt="redirectIcon" />
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-between">
            <div className="w-48 text-md py-2 text-white">Backend URL</div>
            <div className="w-full flex justify-between bg-gray-900 px-4 py-2 border mb-4 rounded">
              <Link
                to={backendUrl}
                target="_blank"
                className="text-indigo-600 hover:text-indigo-200"
                onClick={(e) => e.stopPropagation()}
              >
                {backendUrl}
              </Link>
              <div className="flex">
                <button
                  className="w-10 rounded-full grid place-items-center hover:bg-gray-700/50"
                  onClick={() => handleCopyToClipboard(backendUrl, BACKEND_URL)}
                >
                  <img
                    src={
                      shouldShowTickOnCopy === BACKEND_URL
                        ? whiteTickIcon
                        : whiteCopyIcon
                    }
                    className="w-4"
                    alt={shouldShowTickOnCopy ? "tick-icon" : "copy-icon"}
                  />
                </button>
                <button
                  className="w-10 rounded-full grid place-items-center hover:bg-gray-700/50"
                  onClick={() => window.open(backendUrl, "_blank")}
                >
                  <img src={redirectIcon} className="w-4" alt="redirectIcon" />
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-between">
            <div className="w-48 text-md py-2 text-white">Last Deployment</div>
            <div className=" w-full bg-gray-900 text-white px-4 py-2 border mb-4 rounded">
              {getFormattedDate(updateDate)}
            </div>
          </div>
        </div>
      ) : (
        <div className=" bg-gray-800 p-6 rounded-lg shadow-md w-full mb-4 text-center flex flex-col justify-center gap-2 border-2 border-white">
          <div className="text-white font-bold mb-4 text-2xl flex flex-col items-center justify-center gap-2">
            {shouldShowDeployUrl === IN_PROGRESS ? (
              <Spinner
                showSpinner={true}
                loaderComponent={<LoaderComponent />}
              />
            ) : (
              <img src={errorIcon} alt="errorIcon" />
            )}
            Deployed URLs
          </div>

          <div className="text-white">
            {shouldShowDeployUrl === FAILURE ? (
              <div>
                There is a issue in deployment. Please fix the issue or try
                <span
                  onClick={() => navigate(`/applications/${appId}/settings`)}
                  className="text-blue-500 cursor-pointer"
                >
                  {" re-deploying "}
                </span>
                again.
              </div>
            ) : (
              <div className="animate-pulse">
                Deployed URLs will be generated after successful deployment
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const RenderDataModalDetails = ({ dataModal }) => {
  const navigate = useNavigate();

  const cardDetails = Object.keys(dataModal).reduce((obj, property) => {
    if (RequiredCards.includes(property))
      return { ...obj, [property]: dataModal[property] };
    else return obj;
  }, {});

  const getNavigate = (key) => {
    if (key === "backgroundTasks")
      navigate(`/data-models/${dataModal.id}/background-tasks`);
    else navigate(`/data-models/${dataModal.id}/${key}`);
  };

  return (
    !isEmpty(cardDetails) && (
      <>
        {Object.entries(cardDetails).map(([key, value]) => (
          <div
            className="flex items-center w-full bg-gray-800 justify-center gap-4 rounded-lg cursor-pointer hover:border-2 hover:border-indigo-600"
            key={key}
            onClick={() => getNavigate(key)}
          >
            <div className="text-white font-bold text-lg">{value.length}</div>
            <div className="capitalize text-white text-lg">
              {key.replace(UPDATE_CASE, "$1 $2")}
            </div>
          </div>
        ))}
      </>
    )
  );
};

const RenderDeploymentLinks = ({
  appId,
  updateDate,
  frontendUrl,
  backendUrl,
}) => {
  
  const selectedAppDeployStatus = useSelector(selectAppDeployStatus);
  const [shouldShowDeployUrl, setShouldShowDeployUrl] = useState();

  useEffect(() => {
    if (!selectedAppDeployStatus) return;
    const { frontend, backend } = selectedAppDeployStatus;
    if (frontend === IN_PROGRESS || backend === IN_PROGRESS)
      setShouldShowDeployUrl(IN_PROGRESS);
    else if (frontend === SUCCESS && backend === SUCCESS)
      setShouldShowDeployUrl(SUCCESS);
    else if (
      deploymentFailureStatus.includes(frontend) ||
      deploymentFailureStatus.includes(backend)
    )
      setShouldShowDeployUrl(FAILURE);
  }, [selectedAppDeployStatus]);

  return (
    <div className="py-5 h-min">
      {!shouldShowDeployUrl ? (
        <div className=" bg-gray-800 p-6 rounded-lg shadow-md w-full mb-4 text-center flex flex-col justify-center gap-2 border-2 border-white">
          <div className="text-white font-bold mb-4 text-2xl flex flex-col items-center justify-center gap-2">
            <div>
              <Spinner
                showSpinner={true}
                loaderComponent={<LoaderComponent />}
              />
              <p className="text-lg mt-2">Fetching the deployed URLs</p>
            </div>
          </div>
        </div>
      ) : (
        <RenderDeploymentStatus
          shouldShowDeployUrl={shouldShowDeployUrl}
          frontendUrl={frontendUrl}
          backendUrl={backendUrl}
          appId={appId}
          updateDate={updateDate}
        />
      )}
    </div>
  );
};

const AppDataModal = () => {
  const { appId } = useParams();
  const dispatch = useDispatch();

  const selectedApp = useSelector(selectSelectedApp);
  const selectedDataModel = useSelector(selectSelectedDataModel);

  const frontendUrl = get(
    selectedApp,
    "applicationJson.properties.deployedSiteUrl",
    EMPTY_STRING
  );
  const backendUrl = get(
    selectedApp,
    "applicationJson.properties.deployedBackendUrl",
    EMPTY_STRING
  );
  const updateDate = get(selectedApp, "createdAt", EMPTY_STRING);

  useEffect(() => {
    appId && dispatch(setSelectedAppId(appId));
  }, [dispatch, appId]);

  useEffect(() => {
    if (selectedApp) {
      dispatch(setSelectedTab(selectedApp));
      dispatch(setRecentlySelectedApp(selectedApp));
    }
  }, [selectedApp, dispatch]);

  return (
    <div className="scrollbar content overflow-auto p-6 flex flex-1 flex-col gap-6 px-6 py-4">
      <Helmet>
        <title>DataModals | TavaJS</title>
      </Helmet>
      <div className="flex-col xs:flex-row flex mb-4 gap-4">
        <button
          type="button"
          className="btn btn-primary w-full xs:w-auto ml-auto"
          onClick={() => {
            dispatch(setShouldShowDeploymentModal(true));
          }}
        >
          Show Deploy Logs
        </button>
      </div>
      <Spinner name={[FETCH_APPLICATION_BY_ID, FETCH_DATA_MODELS]}>
        {selectedDataModel ? (
          <div className="w-full">
            <RenderDeploymentLinks
              appId={appId}
              updateDate={updateDate}
              frontendUrl={frontendUrl}
              backendUrl={backendUrl}
            />
            <div className="flex gap-4 h-32">
              <RenderDataModalDetails dataModal={selectedDataModel} />
            </div>
          </div>
        ) : (
          <EmptyLayout
            title="No Database Found"
            subTitle="You can get started by creating a new Database"
            shouldShowButton={false}
            image={dbSidePanelIcon}
          />
        )}
      </Spinner>
    </div>
  );
};
export default AppDataModal;
